import { FontAwesome, FontAwesome6 } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { router, useFocusEffect, useNavigation } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import WebView from 'react-native-webview';

import { useCreateRedirect } from '@/api/hooks/authQueryHooks';
import { useGetAllShows, useGetInfluencerDiscountAsync, useGetUserPurchasedShows } from '@/api/hooks/showHooks';
import { InfluencerDiscount } from '@/api/models/showModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import Text from '@/components/Text';
import Title from '@/components/Title';
import Countdown from '@/components/trbShow/Countdown';
import { APPSFLYER_CONFIG } from '@/constants/config';
import { BLUR_HASH } from '@/constants/layout';
import { useAuth } from '@/contexts/authContext';
import { useColor } from '@/hooks/useColor';
import { applyDiscount } from '@/utils/applyDiscount';
import { AppStorage } from '@/utils/AppStorage';
import { encryptObject } from '@/utils/crypto';
import { formatDateYear } from '@/utils/dateFormats';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

const Purchase = () => {
  const { dispatch } = useAuth();
  const navigation = useNavigation();
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const colors = useColor();

  // Styles for header and WebView
  const safeAreaAdjustedHeight = height - insets.bottom - insets.top;

  const headerHeight = Platform.OS === 'android' ? safeAreaAdjustedHeight * 0.75 : '75%';
  const webViewContainerHeight = Platform.OS === 'android' ? safeAreaAdjustedHeight * 0.75 * 0.8 : '80%';

  const {
    data: userPurchsaedShows,
    isPending: userShowsPending,
    refetch: refetchUserPurchasedShows
  } = useGetUserPurchasedShows();
  const { data: show, isPending: allShowsPending } = useGetAllShows();
  const { mutateAsync: getInfluencerDiscountAsync, isPending: influencerDiscountPending } =
    useGetInfluencerDiscountAsync();
  const { mutateAsync: createRedirect } = useCreateRedirect();

  const [influencerDiscountState, setInfluencerDiscountState] = useState<InfluencerDiscount>();
  const [showPlayer, setShowPlayer] = useState(false);

  const now = new Date();
  const beDate = show?.releaseDate?.replace(' ', 'T');

  const url = APPSFLYER_CONFIG.af_web_dp;
  const deepLinkValue = new URLSearchParams({
    deep_link_value: `muxPlayer?playbackId=${process.env.EXPO_PUBLIC_TRB_TRAILER_PLAYBACK_ID}`
  });
  const webViewRef = useRef<WebView>(null);

  const openWebBrowserForIos = async (url: string) => {
    // Pause the video when openning WebBrowser (focusEffect doesn't get triggered)
    webViewRef.current?.injectJavaScript(`window.postMessage({ type: 'pause' }, '*');`);
    setShowPlayer(false);
    const result = await WebBrowser.openBrowserAsync(url);

    // When the user dismiss the WebBrowser
    if (result.type === 'dismiss' || result.type === 'cancel') {
      refetchUserPurchasedShows();
    }
  };

  useFocusEffect(
    useCallback(() => {
      // Screen gains focus again
      refetchUserPurchasedShows();

      // Screen lost focus
      return () => {
        // Send pause command to WebView
        webViewRef.current?.injectJavaScript(`window.postMessage({ type: 'pause' }, '*');`);
        setShowPlayer(false);
      };
    }, [refetchUserPurchasedShows])
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={show?.title ?? 'The Religion Business'} />
    });
  }, [navigation, show]);

  useEffect(() => {
    // Already going to buy
    dispatch({
      type: 'BUYING_SHOW',
      isBuyingShow: false
    });
    const getInfluencerCode = async () => {
      const value = await AppStorage.getItem('influencerCode');
      if (value) {
        getInfluencerDiscountAsync(
          { influencerCode: value },
          {
            onSuccess: (data) => {
              setInfluencerDiscountState(data);
            }
          }
        );
      } else {
        setInfluencerDiscountState(undefined);
      }
    };

    getInfluencerCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to show if is already purchased and show has been released
  useEffect(() => {
    if (userPurchsaedShows && userPurchsaedShows.length !== 0 && show) {
      if (!beDate) return;
      const releaseDate = new Date(beDate);
      const isReleased = now.getTime() >= releaseDate.getTime();

      if (isReleased) {
        router.replace({
          pathname: '/show',
          params: {
            showId: show.id
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beDate, show, userPurchsaedShows]);

  const diffInSeconds =
    beDate !== undefined ? Math.floor((new Date(beDate).getTime() - now.getTime()) / 1000) : undefined;

  const onPurchase = async () => {
    if (show) {
      // Making purchase outside iOS App
      if (Platform.OS === 'ios') {
        createRedirect(undefined, {
          onSuccess: async (redirect) => {
            const url = APPSFLYER_CONFIG.af_web_dp;

            const showFinalPrice =
              influencerDiscountState !== undefined &&
              applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !== show.bsAppPrice
                ? applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent)
                : show.bsAppPrice;

            const payload = {
              oneTimeToken: redirect.token,
              routeWithSelectedMethod: '(app)/confirmPurchaseShow',
              availableMethods: 'BOTH',
              showId: show.id,
              showTitle: show.title,
              showPrice: showFinalPrice,
              fromIOS: true,
              influencerCode: influencerDiscountState?.discountCode
            };
            const encrypted = await encryptObject(payload);

            const deepLinkValue = new URLSearchParams({
              deep_link_value: `paymentMethods?payload=${encodeURIComponent(encrypted)}`
            });

            openWebBrowserForIos(`${url}?${deepLinkValue}`);
          },
          onError: (e) => {
            console.error(e);
          }
        });
      } else {
        const payload = {
          routeWithSelectedMethod: '(app)/confirmPurchaseShow',
          availableMethods: 'BOTH',
          showId: show.id,
          influenceerCode: influencerDiscountState?.discountCode
        };

        const encrypted = await encryptObject(payload);
        router.navigate({
          pathname: '/paymentMethods',
          params: {
            payload: encodeURIComponent(encrypted)
          }
        });
      }
    }
  };

  if (userShowsPending || allShowsPending || influencerDiscountPending) {
    return (
      <View style={styles.loaderContainer}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <SafeAreaView edges={['bottom']} style={{ ...styles.flexOne, backgroundColor: colors.background }}>
      <View style={styles.mainContainer}>
        <View style={{ ...styles.header, height: headerHeight }}>
          <Text fontWeight="600" style={styles.subtitle}>
            {show?.description}
          </Text>
          <Text style={styles.releaseDate}>
            {i18n.t('trb_show.release_date', { DATE: formatDateYear(show?.releaseDate ?? '', 'MMMM do, yyyy') })}
          </Text>
          <Text
            fontWeight="600"
            style={[
              styles.price,
              {
                ...(influencerDiscountState &&
                  !influencerDiscountPending &&
                  influencerDiscountState.discountPercent > 0 &&
                  show &&
                  show.bsAppPrice &&
                  applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !==
                    show.bsAppPrice && {
                    textDecorationLine: 'line-through'
                  })
              }
            ]}>
            {i18n.t('trb_show.normal_price', { PRICE: show?.bsAppPrice })}
          </Text>
          {influencerDiscountState &&
            !influencerDiscountPending &&
            influencerDiscountState.discountPercent > 0 &&
            show &&
            show.bsAppPrice &&
            applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent) !== show.bsAppPrice && (
              <Text fontWeight="900" style={styles.specialPrice}>
                {i18n.t('trb_show.special_price', {
                  PRICE: applyDiscount(parseFloat(show.bsAppPrice), influencerDiscountState.discountPercent)
                })}
              </Text>
            )}
          <Text style={styles.taxes}>{i18n.t('trb_show.taxes_included')}</Text>
          <Text fontWeight="900" style={styles.trailer}>
            {i18n.t('trb_show.watch_trailer')}
          </Text>
          <View style={{ ...styles.webViewContainer, height: webViewContainerHeight }}>
            {!showPlayer && (
              <Pressable onPress={() => setShowPlayer(true)} style={styles.playPressable}>
                <Image
                  placeholder={BLUR_HASH}
                  style={StyleSheet.absoluteFill}
                  source={{ uri: firebaseImage(safeJsonParseArray(show?.thumbnail ?? '[]').at(0)) }}
                  contentFit="cover"
                />
                <View style={styles.playIconContainer}>
                  <FontAwesome name="play-circle" size={64} color="white" />
                </View>
              </Pressable>
            )}
            {showPlayer && (
              <WebView
                androidLayerType="none"
                ref={webViewRef}
                style={styles.webView}
                containerStyle={styles.webView}
                bounces={false}
                showsVerticalScrollIndicator={false}
                startInLoadingState
                renderLoading={() => (
                  <View style={styles.loaderContainer}>
                    <DotsLoader />
                  </View>
                )}
                originWhitelist={['*']}
                source={{ uri: `${url}?${deepLinkValue}` }}
                contentMode="mobile"
                allowsInlineMediaPlayback
                allowsFullscreenVideo
                allowsAirPlayForMediaPlayback
                mediaPlaybackRequiresUserAction={false}
                javaScriptEnabled
                domStorageEnabled
                removeClippedSubviews
              />
            )}
          </View>
        </View>

        {/* If the show is purchased show countdown to release date */}
        {(userPurchsaedShows === undefined || userPurchsaedShows?.length === 0) &&
        show &&
        (!userShowsPending || !allShowsPending) ? (
          <BrandedButton style={styles.button} onPress={onPurchase}>
            <View style={styles.purchase}>
              <Text fontWeight="700" style={styles.purchaseText}>
                {i18n.t('trb_show.purchase')}
              </Text>
              <FontAwesome6 name="dollar-sign" size={18} color="white" />
            </View>
          </BrandedButton>
        ) : (
          <View style={styles.countdownContainer}>
            <Text fontWeight="700" style={styles.label}>
              {i18n.t('trb_show.release_date', { DATE: formatDateYear(show?.releaseDate ?? '', 'MMMM do, yyyy') })}
            </Text>
            <Countdown
              until={diffInSeconds ?? 0}
              size={24}
              onFinish={() => {
                router.replace({
                  pathname: '/show',
                  params: {
                    showId: show?.id
                  }
                });
              }}
            />
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  mainContainer: {
    paddingHorizontal: 18,
    paddingBottom: 10,
    justifyContent: 'space-between',
    gap: 10,
    alignItems: 'center',
    width: '100%',
    height: '100%'
  },

  container: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  },

  header: {
    width: '100%',
    height: '75%',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 5,
    textAlign: 'center'
  },

  subtitle: {
    fontSize: 18
  },

  releaseDate: {
    fontSize: 16
  },

  price: {
    color: 'black',
    fontSize: 16
  },

  taxes: {
    color: 'black',
    fontSize: 13
  },

  specialPrice: {
    color: 'green',
    fontSize: 18
  },

  trailer: {
    fontSize: 26,
    color: 'black'
  },

  webViewContainer: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: 10
  },

  webView: {
    flex: 1,
    zIndex: 10
  },

  button: {
    width: '100%'
  },

  purchase: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    justifyContent: 'center'
  },

  purchaseText: {
    color: 'white',
    textAlignVertical: 'center'
  },

  countdownContainer: {
    width: '100%',
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    borderRadius: 10
  },

  label: {
    color: '#fff',
    fontSize: 20,
    marginBottom: 10
  },

  playPressable: {
    position: 'relative',
    width: '100%',
    height: '100%'
  },

  playIconContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -32 }, { translateY: -32 }], // center the 64px icon
    zIndex: 1
  }
});

export default Purchase;
